import { Shield, Eye, Lock, UserCheck, Database, Mail, FileText } from 'lucide-react';
import SEO from '../components/SEO';
import { COMPLIANCE_DISCLOSURE_TEXT, LEGAL_CONSENT_DOCUMENT_PATH } from '../constants/site';

const Privacy = () => {
  return (
    <div className="min-h-screen pt-20">
      <SEO 
        title="Privacy Policy | How We Protect Your Information"
        description="Flash Funding's Privacy Policy. Learn how we collect, use, and protect your personal information in accordance with the Privacy Act 1988 and Australian Privacy Principles."
        keywords="Flash Funding privacy policy, privacy Australia, personal information, data protection, Australian Privacy Principles, credit information privacy"
        canonical="/privacy"
      />
      {/* Hero Section */}
      <section className="py-16 lg:py-20 bg-navy-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="font-heading font-black text-4xl sm:text-5xl text-white uppercase tracking-tight leading-none mb-4">
              Privacy<br />
              <span className="gold-gradient-text">Policy</span>
            </h1>
            <p className="font-body text-lg text-white/70">
              How we collect, use, and protect your personal information.
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 lg:py-24 bg-navy-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-8">
            {/* Introduction */}
            <div className="bg-navy-700 border border-white/5 p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-lg bg-gold-500/10 flex items-center justify-center flex-shrink-0">
                  <Shield size={24} className="text-gold-500" />
                </div>
                <div>
                  <h2 className="font-heading font-bold text-2xl text-white uppercase tracking-tight">
                    Our Commitment to Privacy
                  </h2>
                </div>
              </div>
              <div className="space-y-4 font-body text-white/70 leading-relaxed">
                <p>
                  {COMPLIANCE_DISCLOSURE_TEXT}
                </p>
                <p>
                  This Privacy Policy explains how we collect, use, disclose, and protect your personal information when you use our services or interact with our website.
                </p>
                <p>
                  The latest combined Credit Guide &amp; Privacy document is available here:{' '}
                  <a href={LEGAL_CONSENT_DOCUMENT_PATH} target="_blank" rel="noopener noreferrer" className="text-gold-500 hover:underline" data-testid="privacy-consent-document-link">
                    Download Credit Guide &amp; Privacy (PDF)
                  </a>
                </p>
              </div>
            </div>

            {/* Information We Collect */}
            <div className="bg-navy-700 border border-white/5 p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-lg bg-gold-500/10 flex items-center justify-center flex-shrink-0">
                  <Database size={24} className="text-gold-500" />
                </div>
                <div>
                  <h2 className="font-heading font-bold text-2xl text-white uppercase tracking-tight">
                    Information We Collect
                  </h2>
                </div>
              </div>
              <div className="space-y-4 font-body text-white/70 leading-relaxed">
                <p>We may collect the following types of personal information:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Name, address, and contact details</li>
                  <li>Date of birth and identification documents</li>
                  <li>Financial information including income, assets, and liabilities</li>
                  <li>Employment details and business information</li>
                  <li>Credit history and credit report information</li>
                  <li>Bank account and transaction details</li>
                  <li>Information about assets you wish to finance</li>
                </ul>
              </div>
            </div>

            {/* How We Use Information */}
            <div className="bg-navy-700 border border-white/5 p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-lg bg-gold-500/10 flex items-center justify-center flex-shrink-0">
                  <Eye size={24} className="text-gold-500" />
                </div>
                <div>
                  <h2 className="font-heading font-bold text-2xl text-white uppercase tracking-tight">
                    How We Use Your Information
                  </h2>
                </div>
              </div>
              <div className="space-y-4 font-body text-white/70 leading-relaxed">
                <p>We use your personal information to:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Assess your finance application and creditworthiness</li>
                  <li>Provide you with finance products and services</li>
                  <li>Communicate with you about your application or account</li>
                  <li>Comply with our legal and regulatory obligations</li>
                  <li>Improve our products and services</li>
                  <li>Send you marketing communications (with your consent)</li>
                </ul>
              </div>
            </div>

            {/* Disclosure */}
            <div className="bg-navy-700 border border-white/5 p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-lg bg-gold-500/10 flex items-center justify-center flex-shrink-0">
                  <UserCheck size={24} className="text-gold-500" />
                </div>
                <div>
                  <h2 className="font-heading font-bold text-2xl text-white uppercase tracking-tight">
                    Disclosure of Information
                  </h2>
                </div>
              </div>
              <div className="space-y-4 font-body text-white/70 leading-relaxed">
                <p>We may disclose your personal information to:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Credit providers and lenders on our panel</li>
                  <li>Credit reporting bodies</li>
                  <li>AFAS Group Pty Ltd (our licensee)</li>
                  <li>Professional advisers and service providers</li>
                  <li>Government and regulatory authorities as required by law</li>
                </ul>
                <p className="mt-4">
                  We do not sell your personal information to third parties for marketing purposes.
                </p>
              </div>
            </div>

            {/* Security */}
            <div className="bg-navy-700 border border-white/5 p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-lg bg-gold-500/10 flex items-center justify-center flex-shrink-0">
                  <Lock size={24} className="text-gold-500" />
                </div>
                <div>
                  <h2 className="font-heading font-bold text-2xl text-white uppercase tracking-tight">
                    Data Security
                  </h2>
                </div>
              </div>
              <div className="space-y-4 font-body text-white/70 leading-relaxed">
                <p>
                  We take reasonable steps to protect your personal information from misuse, interference, loss, unauthorised access, modification, or disclosure. This includes:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Secure data storage and encryption</li>
                  <li>Access controls and authentication measures</li>
                  <li>Regular security assessments</li>
                  <li>Staff training on privacy and data protection</li>
                </ul>
              </div>
            </div>

            {/* Your Rights */}
            <div className="bg-navy-700 border border-white/5 p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-lg bg-gold-500/10 flex items-center justify-center flex-shrink-0">
                  <FileText size={24} className="text-gold-500" />
                </div>
                <div>
                  <h2 className="font-heading font-bold text-2xl text-white uppercase tracking-tight">
                    Your Rights
                  </h2>
                </div>
              </div>
              <div className="space-y-4 font-body text-white/70 leading-relaxed">
                <p>You have the right to:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Access your personal information we hold</li>
                  <li>Request correction of inaccurate information</li>
                  <li>Opt-out of marketing communications</li>
                  <li>Make a privacy complaint</li>
                </ul>
                <p className="mt-4">
                  To exercise these rights or make a complaint, please contact us at{' '}
                  <a href="mailto:admin@flashfunding.loan" className="text-gold-500 hover:underline">
                    admin@flashfunding.loan
                  </a>
                </p>
              </div>
            </div>

            {/* Contact */}
            <div className="bg-navy-700 border border-gold-500/20 p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-lg bg-gold-500/10 flex items-center justify-center flex-shrink-0">
                  <Mail size={24} className="text-gold-500" />
                </div>
                <div>
                  <h2 className="font-heading font-bold text-2xl text-white uppercase tracking-tight">
                    Contact Us
                  </h2>
                </div>
              </div>
              <div className="space-y-2 font-body text-white/70">
                <p>For privacy-related enquiries:</p>
                <p className="text-white">Flash Funding</p>
                <p>{COMPLIANCE_DISCLOSURE_TEXT}</p>
                <p>Email: <a href="mailto:admin@flashfunding.loan" className="text-gold-500 hover:underline">admin@flashfunding.loan</a></p>
                <p>Phone: <a href="tel:+611800003527" className="text-gold-500 hover:underline font-mono">1800 003 527</a></p>
              </div>
            </div>
          </div>

          {/* Last Updated */}
          <p className="text-white/40 font-body text-sm text-center mt-12">
            Last updated: December 2025
          </p>
        </div>
      </section>
    </div>
  );
};

export default Privacy;
